import UserProfileCard from "../../components/UserProfileCard";
import HowItWorks from "../../components/Dashboard/HowItWorks";
import { Link, useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCallback, useEffect, useState } from "react";
import { getAllProjects } from "../../api/Projects";
import { Project } from "../../utils/interface/project";
import dummyProjectImage from "../../assets/Projects/project1.png";
import userImg from "../../assets/userImg.svg";
import fileImg from "../../assets/file.svg";
const DUMMY_IMAGE_FALLBACK = dummyProjectImage;

const TrackProgressIcon = () => (
  <svg
    className="w-7 transition-transform duration-500 ease-in-out group-hover:rotate-180"
    xmlns="http://www.w3.org/2000/svg"
    width="27.164"
    height="27.154"
    viewBox="0 0 27.164 27.154"
  >
    <defs>
      <clipPath id="dashboard-track-clip">
        <rect width="27.164" height="27.154" fill="#fff" />
      </clipPath>
    </defs>
    <g clipPath="url(#dashboard-track-clip)">
      <path
        d="M475.85,8.9l-5.837,1.9a7.459,7.459,0,0,0-6.423-4.662V0a13.589,13.589,0,0,1,12.26,8.9"
        transform="translate(-449.509)"
        fill="currentColor"
        className="text-white group-hover:text-[#E761A0]"
      />
      <path
        d="M39.39,0V6.137A7.461,7.461,0,0,0,32.966,10.8L27.13,8.9A13.589,13.589,0,0,1,39.39,0"
        transform="translate(-26.306)"
        fill="currentColor"
        className="text-white group-hover:text-[#E761A0]"
      />
      <path
        d="M613.3,328.051a13.558,13.558,0,0,1-5.2,10.689l-3.6-4.959a7.468,7.468,0,0,0,2.452-7.555l5.836-1.9a13.589,13.589,0,0,1,.516,3.721"
        transform="translate(-586.14 -314.479)"
        fill="currentColor"
        className="text-white group-hover:text-[#E761A0]"
      />
      <path
        d="M8.8,333.771,5.2,338.73A13.6,13.6,0,0,1,.516,324.32l5.836,1.9A7.467,7.467,0,0,0,8.8,333.771"
        transform="translate(0 -314.469)"
        fill="currentColor"
        className="text-white group-hover:text-[#E761A0]"
      />
      <path
        d="M212.911,659.749a13.589,13.589,0,0,1-15.152,0l3.6-4.958a7.461,7.461,0,0,0,7.946,0Z"
        transform="translate(-191.753 -634.902)"
        fill="currentColor"
        className="text-white group-hover:text-[#E761A0]"
      />
    </g>
  </svg>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projectList, setProjectsList] = useState<Project[]>([]);

  const fetchProjects = useCallback(
    async (page: number = 1, search: string = "") => {
      setLoading(true);
      try {
        const resp = await getAllProjects(page, 2, search);
        if (resp) {
          const { projects } = resp || {};
          setProjectsList(projects || []);
        }
      } catch (error) {
        setProjectsList([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return (
    <div className=" ">
      <UserProfileCard />

      <div className="heading-wrap mt-2">
        <h1 className="text-[34px] lg:text-[38px] xl:text-[56px] font-display font-weight-300 text-white mb-2 truncate">
          Hello
          {userDetails?.fullName ? (
            <span className="font-weight-600 text-[#E761A0]">
              {", "}
              {userDetails.fullName}
            </span>
          ) : null}
        </h1>

        <p className="text-white font-weight-300  text-[14px] lg:text-[16px] xl:text-[18px]">
          Welcome to BuildCompliance, helping improve how building projects
          manage and verify asset data by addressing pain points, enabling users
          to quickly identify and rectify non-compliant information.
        </p>
      </div>

      {userDetails?.type === "2" && <HowItWorks />}

      {!loading && projectList.length > 0 && (
        <div className="rounded-[18px] overflow-hidden border border-[#868686] 2xl:p-[35px_35px] lg:p-[20px_20px_25px] p-[20px_20px_20px] mt-6">
          {/* Header: Projects (pink) + VIEW MORE (white, pink underline) */}
          <div className="flex items-center justify-between  mb-6">
            <h2 className="text-[20px] lg:text-[26px] xl:text-[29px] font-weight-600 text-[#E761A0]">
              Projects
            </h2>
            <Link
              to="/project-management"
              className="text-[14px] md:text-base font-semibold text-white pb-1.5 uppercase border-b border-[#E761A0] transition-opacity hover:opacity-90"
            >
              View more
            </Link>
          </div>

          {/* Project cards – static 2-col design */}
          <div className="grid grid-cols-1 2xl:grid-cols-2 lg:grid-cols-2 gap-4 xl:gap-8">
            {projectList.map((project) => (
              <div key={project.id} className=" ">
                <div className="md:flex gap-6 md:mb-0 mb-8">
                  {/* Image – left, from assets/Projects */}
                  <div className="md:w-[40%] md:min-w-[140px] shrink-0 overflow-hidden rounded-[18px] bg-gray-700/50 aspect-[4/3]">
                    {(project.projectThumbnailPreview ??
                      project.projectThumbnail) ? (
                      <img
                        src={
                          (project.projectThumbnailPreview ??
                            project.projectThumbnail) ||
                          undefined
                        }
                        alt={project.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            DUMMY_IMAGE_FALLBACK;
                        }}
                      />
                    ) : (
                      <img
                        src={DUMMY_IMAGE_FALLBACK}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  {/* Content – right */}
                  <div className="flex flex-col flex-1 min-w-0 md:mt-0 mt-4">
                    <h3 className="text-[22px] font-weight-600 text-white leading-tight mb-2">
                      {project.name}
                    </h3>
                    <p className="text-[18px] text-white leading-snug line-clamp-3 flex-1 font-weight-300">
                      {project.summary}
                    </p>
                    {userDetails?.type === "0" ? (
                      <Link
                        to={`/ifc-processing/${project.id}`}
                        className="mt-4 group overflow-hidden flex items-center gap-2 px-5 py-[6px] rounded-[27px] border-2 border-[#E761A0] bg-[#E761A0] hover:text-[#E761A0] text-white transition-all duration-300 ease-out hover:bg-transparent btn-hover-color max-w-[230px] uppercase font-weight-600 xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px] justify-center"
                      >
                        <TrackProgressIcon />
                        Update progress
                      </Link>
                    ) : (
                      <Link
                        to={`/project-management/view/${project.id}`}
                        className="mt-4 group overflow-hidden flex items-center gap-2 px-5 py-[6px]
 rounded-[27px] border-2 border-[#E761A0]
 bg-[#E761A0] hover:text-[#E761A0] text-white
 transition-all duration-300 ease-out
 hover:bg-transparent btn-hover-color max-w-[226px] uppercase font-weight-600 xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px] justify-center"
                      >
                        <Eye className="w-6 h-6 text-white group-hover:text-[#E761A0]" />
                        View project
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {userDetails?.type === "0" && (
        <div>
          <div className="sm:flex items-center gap-3 md:gap-8  lg:mt-8 mt-5">
            <div className="  text-white  rounded-[18px] sm:max-w-[226px] text-center">
              <img
                src={userImg}
                alt="userImg"
                className="mx-auto flex w-[90px] h-[90px] mb-5"
              />
              <button
                type="button"
                onClick={() => navigate("/user-management")}
                className="mt-4 group overflow-hidden flex items-center gap-2 px-5 py-[6px]
 rounded-[27px] border-2 border-[#E761A0]
 bg-[#E761A0] hover:text-[#E761A0] text-white
 transition-all duration-300 ease-out
 hover:bg-transparent btn-hover-color sm:max-w-[226px]  uppercase font-weight-600 text-center justify-center xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px] sm:mx-0 mx-auto"
              >
                Manage Users
              </button>
            </div>
            <div className="  text-white   rounded-[18px] sm:max-w-[226px] text-center">
              <img
                src={fileImg}
                alt="fileImg"
                className="mx-auto flex w-[90px] h-[90px] mb-5"
              />
              <button
                type="button"
                onClick={() => navigate("/project-management")}
                className="mt-4 group overflow-hidden flex items-center lg:gap-2 px-5 py-[6px]
 rounded-[27px] border-2 border-[#E761A0]
 bg-[#E761A0] hover:text-[#E761A0] text-white
 transition-all duration-300 ease-out
 hover:bg-transparent btn-hover-color sm:max-w-[226px] uppercase font-weight-600 text-center justify-center xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px]  sm:mx-0 mx-auto  "
              >
                Manage Projects
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
